import axios from 'axios';
import apiClient, { getBaseURL } from './apiClient';
import {
  Student,
  StudentProgress,
  StudentEnrollment,
  TeacherStudentRelationship,
  LinkStudentByEmailData,
  GetStudentsResponse,
  GetStudentResponse,
  GetStudentProgressResponse,
  GetStudentEnrollmentsResponse,
  LinkStudentResponse,
  RemoveStudentResponse,
  GetTeacherDashboardStatsResponse,
  StudentTrend,
  ClassTrend,
  StrugglingStudent,
  QuizAnalytics,
  GetStudentTrendsResponse,
  TeacherInvitationToken,
  CreateInvitationTokenData,
  PublicInvitationInfo,
  GetClassTrendsResponse,
  GetStrugglingStudentsResponse,
  GetQuizAnalyticsResponse,
  StudentAttemptAnalytics,
  ComparisonData
} from '../types/teacher';

/**
 * Search for students by name or email
 * @param searchTerm - Search query (name or email)
 * @returns Array of students matching the search
 */
export const searchStudents = async (searchTerm: string): Promise<Student[]> => {
  try {
    if (!searchTerm || searchTerm.trim().length < 2) {
      return [];
    }

    const response = await apiClient.get<GetStudentsResponse>('/api/teacher/students/search', {
      params: { q: searchTerm.trim() }
    });
    return response.data.students;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const errorMessage =
        error.response.data.message ||
        error.response.data.error ||
        'Failed to search students';
      throw new Error(errorMessage);
    }
    throw new Error('Network error. Please try again later.');
  }
};

/**
 * Get all students for the authenticated teacher
 * @returns Array of students (User objects)
 */
export const getStudents = async (): Promise<Student[]> => {
  try {
    const response = await apiClient.get<GetStudentsResponse>('/api/teacher/students');
    return response.data.students;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const errorMessage =
        error.response.data.message ||
        error.response.data.error ||
        'Failed to fetch students';
      throw new Error(errorMessage);
    }
    throw new Error('Network error. Please try again later.');
  }
};

/**
 * Get details for a specific student
 * @param studentId - Student user ID
 * @returns Student user object
 */
export const getStudent = async (studentId: number): Promise<Student> => {
  try {
    const response = await apiClient.get<GetStudentResponse>(
      `/api/teacher/students/${studentId}`
    );
    return response.data.student;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const errorMessage =
        error.response.data.message ||
        error.response.data.error ||
        'Failed to fetch student';
      throw new Error(errorMessage);
    }
    throw new Error('Network error. Please try again later.');
  }
};

/**
 * Get progress metrics for a specific student
 * @param studentId - Student user ID
 * @returns StudentProgress object with metrics
 */
export const getStudentProgress = async (studentId: number): Promise<StudentProgress> => {
  try {
    const response = await apiClient.get<GetStudentProgressResponse>(
      `/api/teacher/students/${studentId}/progress`
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const errorMessage =
        error.response.data.message ||
        error.response.data.error ||
        'Failed to fetch student progress';
      throw new Error(errorMessage);
    }
    throw new Error('Network error. Please try again later.');
  }
};

/**
 * Get enrollments for a specific student
 * @param studentId - Student user ID
 * @returns Array of enrollment objects
 */
export const getStudentEnrollments = async (studentId: number): Promise<StudentEnrollment[]> => {
  try {
    const response = await apiClient.get<GetStudentEnrollmentsResponse>(
      `/api/teacher/students/${studentId}/enrollments`
    );
    return response.data.enrollments;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const errorMessage =
        error.response.data.message ||
        error.response.data.error ||
        'Failed to fetch student enrollments';
      throw new Error(errorMessage);
    }
    throw new Error('Network error. Please try again later.');
  }
};

/**
 * Link an existing student to the authenticated teacher
 * @param studentEmail - Student email address to link
 * @returns Created relationship
 */
export const linkStudent = async (
  studentEmail: string
): Promise<TeacherStudentRelationship> => {
  try {
    const linkData: LinkStudentByEmailData = {
      studentEmail,
      relationshipType: 'teacher'
    };

    const response = await apiClient.post<LinkStudentResponse>(
      '/api/teacher/students',
      linkData
    );
    return response.data.relationship;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const errorMessage =
        error.response.data.message ||
        error.response.data.error ||
        'Failed to link student';
      throw new Error(errorMessage);
    }
    throw new Error('Network error. Please try again later.');
  }
};

/**
 * Remove a student from the authenticated teacher
 * @param studentId - Student user ID to remove
 */
export const removeStudent = async (studentId: number): Promise<void> => {
  try {
    await apiClient.delete<RemoveStudentResponse>(`/api/teacher/students/${studentId}`);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const errorMessage =
        error.response.data.message ||
        error.response.data.error ||
        'Failed to remove student';
      throw new Error(errorMessage);
    }
    throw new Error('Network error. Please try again later.');
  }
};

/**
 * Get aggregated class statistics for the authenticated teacher
 * @returns TeacherDashboardStats object with class-wide metrics
 */
export const getClassStats = async (): Promise<GetTeacherDashboardStatsResponse> => {
  try {
    const response = await apiClient.get<GetTeacherDashboardStatsResponse>('/api/teacher/stats');
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const errorMessage =
        error.response.data.message ||
        error.response.data.error ||
        'Failed to fetch class statistics';
      throw new Error(errorMessage);
    }
    throw new Error('Network error. Please try again later.');
  }
};

/**
 * Get trend data for a specific student
 * @param studentId - Student user ID
 * @param timeWindow - Number of weeks to retrieve (default: 12)
 * @returns Array of weekly trend data for the student
 */
export const getStudentTrends = async (
  studentId: number,
  timeWindow = 12
): Promise<StudentTrend[]> => {
  try {
    const response = await apiClient.get<GetStudentTrendsResponse>(
      `/api/teacher/students/${studentId}/trends`,
      { params: { timeWindow } }
    );
    return response.data.trends;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const errorMessage =
        error.response.data.message ||
        error.response.data.error ||
        'Failed to fetch student trends';
      throw new Error(errorMessage);
    }
    throw new Error('Network error. Please try again later.');
  }
};

/**
 * Get class-wide trend data for analytics
 * @param timeWindow - Number of weeks to retrieve (default: 12)
 * @returns Array of weekly trend data aggregated across all students
 */
export const getClassTrends = async (timeWindow = 12): Promise<ClassTrend[]> => {
  try {
    const response = await apiClient.get<GetClassTrendsResponse>(
      '/api/teacher/analytics/class-trends',
      { params: { timeWindow } }
    );
    return response.data.trends;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const errorMessage =
        error.response.data.message ||
        error.response.data.error ||
        'Failed to fetch class trends';
      throw new Error(errorMessage);
    }
    throw new Error('Network error. Please try again later.');
  }
};

/**
 * Identify struggling students who need intervention
 * @returns Array of students identified as struggling with flags and recommended actions
 */
export const getStrugglingStudents = async (): Promise<StrugglingStudent[]> => {
  try {
    const response = await apiClient.get<GetStrugglingStudentsResponse>(
      '/api/teacher/analytics/struggling-students'
    );
    return response.data.strugglingStudents;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const errorMessage =
        error.response.data.message ||
        error.response.data.error ||
        'Failed to fetch struggling students';
      throw new Error(errorMessage);
    }
    throw new Error('Network error. Please try again later.');
  }
};

/**
 * Get quiz analytics for teacher's class
 * Returns aggregated quiz performance metrics across all students
 * @returns QuizAnalytics object with overall and per-quiz metrics
 */
export const getQuizAnalytics = async (): Promise<QuizAnalytics> => {
  try {
    const response = await apiClient.get<GetQuizAnalyticsResponse>(
      '/api/teacher/analytics/quiz-performance'
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const errorMessage =
        error.response.data.message ||
        error.response.data.error ||
        'Failed to fetch quiz analytics';
      throw new Error(errorMessage);
    }
    throw new Error('Network error. Please try again later.');
  }
};

/**
 * Get student attempt analytics
 * Returns detailed metrics about how efficiently a student solves problems
 * @param studentId - Student user ID
 * @returns StudentAttemptAnalytics object with attempt metrics and per-problem breakdown
 */
export const getStudentAttemptAnalytics = async (studentId: number): Promise<StudentAttemptAnalytics> => {
  try {
    const response = await apiClient.get<StudentAttemptAnalytics>(
      `/api/teacher/students/${studentId}/attempt-analytics`
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const errorMessage =
        error.response.data.message ||
        error.response.data.error ||
        'Failed to fetch student attempt analytics';
      throw new Error(errorMessage);
    }
    throw new Error('Network error. Please try again later.');
  }
};

/**
 * Get comparison data for two students
 * Returns side-by-side metrics to help understand scoring differences
 * @param studentId1 - First student user ID
 * @param studentId2 - Second student user ID
 * @returns ComparisonData with metrics for both students
 */
export const compareStudents = async (studentId1: number, studentId2: number): Promise<ComparisonData> => {
  try {
    const response = await apiClient.get<ComparisonData>(
      `/api/teacher/students/compare`,
      {
        params: { studentIds: `${studentId1},${studentId2}` }
      }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const errorMessage =
        error.response.data.message ||
        error.response.data.error ||
        'Failed to compare students';
      throw new Error(errorMessage);
    }
    throw new Error('Network error. Please try again later.');
  }
};

/**
 * Generate a new invitation link for students to register under this teacher
 */
export const generateInvitationLink = async (
  data?: CreateInvitationTokenData
): Promise<TeacherInvitationToken> => {
  try {
    const response = await apiClient.post<TeacherInvitationToken>(
      '/api/teacher/invitation/generate',
      data || {}
    );
    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response) {
      const errorMessage =
        error.response.data.error ||
        'Failed to generate invitation link';
      throw new Error(errorMessage);
    }
    throw new Error('Network error. Please try again later.');
  }
};

/**
 * Get all invitation tokens for the current teacher
 */
export const getInvitationTokens = async (): Promise<TeacherInvitationToken[]> => {
  try {
    const response = await apiClient.get<{ tokens: TeacherInvitationToken[] }>(
      '/api/teacher/invitation/tokens'
    );
    return response.data.tokens;
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response) {
      const errorMessage =
        error.response.data.error ||
        'Failed to get invitation tokens';
      throw new Error(errorMessage);
    }
    throw new Error('Network error. Please try again later.');
  }
};

/**
 * Revoke an invitation token
 */
export const revokeInvitationToken = async (tokenId: number): Promise<void> => {
  try {
    await apiClient.delete(`/api/teacher/invitation/tokens/${tokenId}`);
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response) {
      const errorMessage =
        error.response.data.error ||
        'Failed to revoke invitation token';
      throw new Error(errorMessage);
    }
    throw new Error('Network error. Please try again later.');
  }
};

/**
 * Validate an invitation token (public endpoint - no auth required)
 */
export const validateInvitationToken = async (
  token: string
): Promise<PublicInvitationInfo> => {
  try {
    // Use axios directly for public endpoint (no auth required)
    // Use getBaseURL() to ensure correct URL in both dev and production
    const baseURL = getBaseURL();
    const url = `${baseURL}/api/teacher/invitation/validate/${token}`;

    console.log('[validateInvitationToken] Validating token:', token.substring(0, 8) + '...');
    console.log('[validateInvitationToken] Using base URL:', baseURL);

    const response = await axios.get<PublicInvitationInfo>(url);

    console.log('[validateInvitationToken] Validation result:', response.data);
    return response.data;
  } catch (error: unknown) {
    console.error('[validateInvitationToken] Error validating token:', error);

    if (axios.isAxiosError(error)) {
      if (error.response) {
        console.error('[validateInvitationToken] Response error:', {
          status: error.response.status,
          data: error.response.data
        });
        const errorMessage =
          error.response.data.error ||
          'Failed to validate invitation token';
        throw new Error(errorMessage);
      } else if (error.request) {
        console.error('[validateInvitationToken] No response received:', error.request);
        throw new Error('Network error: Unable to connect to server. Please check your internet connection.');
      }
    }

    console.error('[validateInvitationToken] Unknown error:', error);
    throw new Error('An unexpected error occurred. Please try again later.');
  }
};

/**
 * Link existing student to teacher via invitation token
 */
export const linkStudentWithInvitation = async (
  token: string
): Promise<{ success: boolean; message: string }> => {
  try {
    console.log('[linkStudentWithInvitation] Linking student with token:', token.substring(0, 8) + '...');

    const response = await apiClient.post<{ success: boolean; message: string }>(
      '/api/teacher/invitation/link',
      { token }
    );

    console.log('[linkStudentWithInvitation] Link successful:', response.data);
    return response.data;
  } catch (error: unknown) {
    console.error('[linkStudentWithInvitation] Error linking student:', error);

    if (axios.isAxiosError(error)) {
      if (error.response) {
        console.error('[linkStudentWithInvitation] Response error:', {
          status: error.response.status,
          data: error.response.data
        });
        const errorMessage =
          error.response.data.error ||
          'Failed to link with teacher';
        throw new Error(errorMessage);
      } else if (error.request) {
        console.error('[linkStudentWithInvitation] No response received:', error.request);
        throw new Error('Network error: Unable to connect to server. Please check your internet connection.');
      }
    }

    console.error('[linkStudentWithInvitation] Unknown error:', error);
    throw new Error('An unexpected error occurred while linking. Please try again later.');
  }
};
